import type { CertStatusActionId } from 'src/types/CertStatus';
import {
  RENTER_PATHS,
  buildInterviewPath,
  buildMyDocumentsPath,
} from 'src/routes/renterPaths';

/**
 * Destination for a cert-status action item.
 *
 * - questions / profile_gaps / insufficient_files → the interview, which
 *   re-presents the pending question (file questions included).
 * - rejected_docs → My Documents, to review/replace the rejected file.
 * - signatures → the signatures page.
 *
 * Returns null when the action needs a survey id we don't have.
 */
export function certStatusActionPath(
  id: CertStatusActionId,
  surveyId: number | null | undefined,
  memberUserId?: number | null
): string | null {
  if (id === 'signatures') {
    return `/${RENTER_PATHS.HOME}/signatures`;
  }
  if (surveyId == null) {
    return null;
  }
  if (id === 'rejected_docs') {
    return buildMyDocumentsPath(surveyId, memberUserId);
  }
  return buildInterviewPath(surveyId, memberUserId);
}

export interface CertStatusActionTarget {
  /** Same URL `certStatusActionPath` returns — unchanged by this helper. */
  path: string | null;
  /** Route state to hand to `navigate`. */
  state?: { requestedWorkItem: 'questions' };
}

/**
 * Navigation target (path + route state) for a cert-status action item.
 *
 * The `questions` action asks for the interview's questions specifically. The
 * interview resolves its first work item signature-first, so a cert that has
 * both a live question group and pending signatures otherwise lands on the
 * signature screen. The intent travels in route state rather than the URL so
 * the interview path stays canonical (buildInterviewPath is a pure helper with
 * pinned outputs).
 */
export function certStatusActionNavigation(
  id: CertStatusActionId,
  surveyId: number | null | undefined,
  memberUserId?: number | null
): CertStatusActionTarget {
  const path = certStatusActionPath(id, surveyId, memberUserId);
  if (id === 'questions' && path != null) {
    return { path, state: { requestedWorkItem: 'questions' } };
  }
  return { path };
}
